/**
 * @typedef {object} AutoOpenOptions
 * @property {string} [selector] - CSS selector for trigger elements.
 * @property {string} [param] - URL search parameter name to check.
 * @property {string} [prefix] - ID prefix to normalize against.
 * @property {number} [delayMs] - Milliseconds to wait before auto-clicking.
 */

const DEFAULTS = {
    selector: '[data-toggle="lux-popup"]',
    param: "open",
    prefix: "lux-modal-",
    delayMs: 1200,
};

/**
 * Normalize a raw ID string by ensuring it starts with the given prefix.
 *
 * @param {string | null | undefined} raw - The raw ID value from the URL.
 * @param {string} prefix - The prefix to prepend if missing.
 * @returns {string | null} The normalized ID, or null if raw is falsy.
 */
function normalizeWantedId(raw, prefix) {
    if (!raw) return null;
    return raw.startsWith(prefix) ? raw : `${prefix}${raw}`;
}

/**
 * Read the wanted popup ID from the current URL's search params or hash.
 *
 * @param {{ param: string, prefix: string }} options - The param and prefix to use.
 * @returns {string | null} The normalized ID, or null if not found.
 */
function readWantedId({ param, prefix }) {
    const url = new URL(location.href);
    const raw = url.searchParams.get(param) ?? location.hash.slice(1);
    return normalizeWantedId(raw, prefix);
}

/**
 * Remove the auto-open param and hash from the current URL without navigation.
 *
 * @param {string} param - The search parameter name to delete.
 * @returns {void}
 */
function cleanupUrl(param) {
    const url = new URL(location.href);
    url.searchParams.delete(param);
    url.hash = "";
    history.replaceState(null, "", url);
}

/**
 * Find the trigger element that matches the wanted popup ID.
 *
 * @param {string} wantedId - The normalized popup ID to look for.
 * @param {string} selector - The base CSS selector for trigger elements.
 * @returns {Element | null} The matching trigger element, or null.
 */
function findTrigger(wantedId, selector) {
    return (
        document.querySelector(`${selector}[data-target="${wantedId}"]`) ||
        document.querySelector(`${selector}[data-target="#${wantedId}"]`) ||
        document.querySelector(`${selector}[href="#${wantedId}"]`)
    );
}

/**
 * Register an auto-open handler that checks the URL for a popup ID and
 * dispatches a click on the matching trigger element after a delay.
 *
 * @param {AutoOpenOptions} [opts] - Override default auto-open options.
 * @returns {void}
 */
export function registerAutoOpenFromUrl(opts = {}) {
    const o = { ...DEFAULTS, ...opts };
    const wantedId = readWantedId(o);

    if (!wantedId) {
        cleanupUrl(o.param);

        return;
    }

    const trigger = findTrigger(wantedId, o.selector);

    if (!trigger) {
        cleanupUrl(o.param);

        return;
    }

    setTimeout(() => {
        trigger.dispatchEvent(
            new MouseEvent("click", { bubbles: true, cancelable: true }),
        );
    }, o.delayMs);
}
